import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/formatters';
import CreateDriver from './CreateDriver';
import CreateVendorFleet from './CreateVendorFleet';
import PasswordInput from './PasswordInput';
import SupportTickets from './SupportTickets';

// Inject Google Inter font once
if (!document.getElementById('vp-font-inter')) {
    const l = document.createElement('link');
    l.id = 'vp-font-inter';
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap';
    document.head.appendChild(l);
}

const API = '/api';

/* ── Icon ── */
const Icon = ({ d, size = 18, color = 'currentColor', strokeWidth = 1.8 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, k) => <path key={k} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    overview: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
    fleet: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    docs: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
    contracts: ['M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2', 'M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2', 'M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2', 'M12 12h3', 'M12 16h3', 'M9 12h.01', 'M9 16h.01'],
    pricing: ['M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    profile: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
    logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
    trips: ['M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3', 'M14 17H9m10 0h3m-3 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm1-10h.01'],
    analytics: ['M3 3v18h18', 'M18 17l-6-6-4 4-4-4'],
    chevron: 'M9 18l6-6-6-6',
    pin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z'],
    phone: ['M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.21 12 19.79 19.79 0 0 1 1.1 3.4A2 2 0 0 1 3.07 1H6a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z'],
    mail: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
    tag: ['M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z', 'M7 7h.01'],
    check: 'M20 6L9 17l-5-5',
    building: ['M3 21h18', 'M3 7v1a3 3 0 0 0 6 0V4', 'M9 8a3 3 0 0 0 6 0V4', 'M15 8a3 3 0 0 0 6 0V7H3'],
    drivers: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    plus: 'M12 5v14M5 12h14',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    x: 'M18 6L6 18 M6 6l12 12',
    save: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8',
    trip: ['M5 12h14', 'M12 5l7 7-7 7'],
    invoice: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M12 18h2', 'M12 14h2', 'M12 10h2', 'M8 18h2', 'M8 14h2', 'M8 10h2'],
    quote: ['M8 21h8a2 2 0 0 0 2-2v-9l-5-5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z', 'M13 5v5h5', 'M8 13h4', 'M8 17h6'],
    bell: ['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
    send: ['M22 2L11 13', 'M22 2L15 22 11 13 2 9l20-7z'],
    alertCircle: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 8v4', 'M12 16h.01'],
    tickets: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
};

const NAV_GROUPS = [
    {
        label: 'Main',
        items: [
            { key: 'overview', label: 'Overview', icon: 'overview' },
            { key: 'trips', label: 'Trips', icon: 'trip', badgeKey: 'pendingTrips' },
        ]
    },
    {
        label: 'Fleet & Drivers',
        items: [
            { key: 'fleet', label: 'Manage Fleet', icon: 'fleet' },
            { key: 'drivers', label: 'Manage Drivers', icon: 'drivers' },
        ]
    },
    {
        label: 'Finance',
        items: [
            { key: 'invoices', label: 'Invoices', icon: 'invoice' },
            { key: 'quotations', label: 'Quotations', icon: 'quote' },
            { key: 'pricing', label: 'Manage Pricing', icon: 'pricing' },
        ]
    },
    {
        label: 'Legal & Info',
        items: [
            { key: 'documents', label: 'Documents', icon: 'docs' },
            { key: 'contracts', label: 'Contracts', icon: 'contracts' },
            { key: 'profile', label: 'Manage Profile', icon: 'profile' },
            { key: 'support', label: 'Support Tickets', icon: 'tickets' },
        ]
    }
];

/* ─────────────────────── SIDEBAR ─────────────────────── */
function VendorSidebar({ activePage, onNavigate, vendor, onLogout, operatorDetails, badges, notifications, onMarkAllRead, onMarkRead, hasActiveContract }) {
    const [showNotifPanel, setShowNotifPanel] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState(() => {
        const initial = {};
        NAV_GROUPS.forEach(g => {
            initial[g.label] = g.items.some(i => i.key === activePage) || g.label === 'Main';
        });
        return initial;
    });

    const toggleGroup = (label) => {
        setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
    };

    const unreadCount = notifications.filter(n => !n.isRead).length;

    const typeColor = (type) => {
        if (type === 'trip_assigned') return '#f59e0b';
        if (type === 'vendor_responded') return '#3b82f6';
        if (type === 'trip_accepted') return '#10b981';
        if (type === 'trip_rejected') return '#ef4444';
        return '#6b7280';
    };

    return (
        <aside className="sidebar superadmin-sidebar" style={sb.root}>
            {/* Brand */}
            <div style={{ padding: '24px 16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ flex: 1, height: '44px', display: 'flex', alignItems: 'center' }}>
                    {operatorDetails?.companyLogo ? (
                        <img
                            src={`/uploads/${operatorDetails.companyLogo}`}
                            alt={operatorDetails?.companyName}
                            style={{ width: '100%', maxWidth: '160px', height: '100%', objectFit: 'contain', objectPosition: 'left' }}
                        />
                    ) : (
                        <div style={{ background: 'linear-gradient(135deg,#10b981,#059669)', width: 44, height: 44, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(16,185,129,0.25)' }}>
                            <Icon d={icons.building} size={22} color="white" />
                        </div>
                    )}
                </div>
                {/* Notification Bell */}
                <div style={{ position: 'relative', flexShrink: 0, marginLeft: 12 }}>
                    <button
                        onClick={() => setShowNotifPanel(p => !p)}
                        style={{ background: unreadCount > 0 ? '#fef3c7' : '#f3f4f6', border: `1px solid ${unreadCount > 0 ? '#fde68a' : '#e5e7eb'}`, cursor: 'pointer', padding: '7px 8px', position: 'relative', display: 'flex', alignItems: 'center', borderRadius: 10 }}
                        title="Notifications"
                    >
                        <Icon d={icons.bell} size={17} color={unreadCount > 0 ? '#d97706' : '#6b7280'} />
                        {unreadCount > 0 && (
                            <span style={{ position: 'absolute', top: -5, right: -5, background: '#ef4444', color: 'white', borderRadius: '50%', width: 17, height: 17, fontSize: 9, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(239,68,68,0.5)' }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                        )}
                    </button>
                    {showNotifPanel && (
                        <div style={{ position: 'fixed', top: 0, left: 256, height: '100vh', width: 340, background: '#ffffff', boxShadow: '4px 0 32px rgba(0,0,0,0.14)', borderLeft: '1px solid #e5e7eb', zIndex: 998, display: 'flex', flexDirection: 'column', fontFamily: FONT }} onClick={e => e.stopPropagation()}>
                            {/* Panel Header */}
                            <div style={{ padding: '24px 20px 16px', background: 'linear-gradient(135deg,#ecfdf5,#f0fdf4)', borderBottom: '1px solid #d1fae5' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 800, fontSize: 16, color: '#064e3b', letterSpacing: '-0.02em' }}>Notifications</div>
                                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        {unreadCount > 0 && (
                                            <button onClick={onMarkAllRead} style={{ fontSize: 11, color: '#059669', background: 'white', border: '1px solid #a7f3d0', borderRadius: 8, cursor: 'pointer', fontWeight: 700, padding: '5px 10px', fontFamily: FONT }}>Mark all read</button>
                                        )}
                                        <button onClick={() => setShowNotifPanel(false)} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                            <Icon d={icons.x} size={14} color="#6b7280" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* Notification List */}
                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                {notifications.length === 0 ? (
                                    <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
                                        <Icon d={icons.bell} size={40} color="#d1fae5" />
                                        <div style={{ marginTop: 12, fontWeight: 700, fontSize: 14, color: '#374151' }}>No notifications yet</div>
                                        <div style={{ marginTop: 4, fontSize: 12 }}>You're all caught up!</div>
                                    </div>
                                ) : notifications.map((n, i) => (
                                    <div key={n._id} onClick={() => {
                                        if (!n.isRead && onMarkRead) onMarkRead(n._id);
                                        setShowNotifPanel(false);
                                        onNavigate('trips');
                                    }} style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', background: n.isRead ? 'white' : '#fffbeb', display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer', transition: 'background 0.2s' }}>
                                        <div style={{ width: 38, height: 38, borderRadius: 12, background: typeColor(n.type) + '20', border: `1.5px solid ${typeColor(n.type)}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: typeColor(n.type) }} />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.35 }}>{n.title}</div>
                                                {!n.isRead && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', flexShrink: 0, marginTop: 4, display: 'block' }} />}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>{n.message}</div>
                                            <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6, fontWeight: 500 }}>{new Date(n.createdAt).toLocaleString()}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>


            {/* Nav */}
            <nav className="sidebar-nav" style={{ flex: 1, padding: '16px 0', overflowY: 'auto' }}>
                {NAV_GROUPS.map(group => {
                    const items = group.items;
                    if (items.length === 0) return null;
                    const isExpanded = expandedGroups[group.label];
                    const isActiveGroup = items.some(item => item.key === activePage);

                    return (
                        <React.Fragment key={group.label}>
                            <div 
                                className={`nav-item ${isActiveGroup ? 'active' : ''}`}
                                onClick={() => toggleGroup(group.label)}
                                style={{ cursor: 'pointer', textTransform: 'uppercase', fontWeight: 800 }}
                            >
                                <span className="nav-icon">
                                    <Icon d={icons[items[0].icon]} size={15} />
                                </span>
                                <span>{group.label}</span>
                                <span
                                    className="nav-chevron"
                                    style={{
                                        marginLeft: 'auto',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        transition: 'transform 0.2s',
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                >
                                    <Icon d={icons.chevron} size={12} />
                                </span>
                            </div>

                            {/* Sub-menu */}
                            {isExpanded && (
                                <div className="nav-submenu">
                                    {items.map(item => {
                                        const active = activePage === item.key;
                                        const badgeCount = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
                                        return (
                                            <div
                                                key={item.key}
                                                className={`nav-item submenu-item ${active ? 'active' : ''}`}
                                                onClick={() => onNavigate(item.key)}
                                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', paddingRight: '24px' }}
                                            >
                                                <span>{item.label}</span>
                                                {badgeCount > 0 && (
                                                    <span style={{ background: '#f59e0b', color: 'white', borderRadius: 10, fontSize: 10, fontWeight: 800, padding: '1px 7px' }}>
                                                        {badgeCount}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </nav>

            {/* Footer */}
            <div className="sidebar-bottom-bar" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', borderTop: '1px solid #f1f5f9' }}>
                <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 14, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', fontWeight: 800, color: '#0f172a' }}>
                    {vendor?.vendorName?.charAt(0) || 'V'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#0f172a', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {vendor?.vendorName || 'Vendor'}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        Managed by {operatorDetails?.companyName || 'Opsmaticz'}
                    </div>
                </div>
                <button
                    onClick={onLogout}
                    title="Logout"
                    style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', borderRadius: '6px', transition: 'background 0.2s' }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    <Icon d={icons.logout} size={16} />
                </button>
            </div>
        </aside>
    );
}

const FONT = "'Inter',system-ui,sans-serif";

const sb = {
    root: {
        width: 260, minHeight: '100vh',
        background: '#ffffff',
        borderRight: '1px solid #f1f5f9',
        display: 'flex', flexDirection: 'column',
        fontFamily: FONT, flexShrink: 0,
        boxShadow: '4px 0 24px rgba(0,0,0,0.02)',
    },
    brand: {
        padding: '28px 24px 20px', display: 'flex', alignItems: 'center', gap: 14,
        borderBottom: '1px solid #dcfce7',
    },
    brandIcon: {
        width: 44, height: 44, borderRadius: 14,
        background: 'linear-gradient(135deg,#10b981,#059669)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 8px 16px rgba(16,185,129,0.25)', flexShrink: 0,
    },
    brandName: { fontSize: 15, fontWeight: 900, color: '#064e3b', letterSpacing: '-0.02em' },
    brandSub: { fontSize: 10, fontWeight: 800, color: '#10b981', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 3 },

    vendorCard: {
        margin: '18px 14px 10px', padding: '14px 16px', borderRadius: 18,
        background: '#ffffff', border: '1px solid #bbf7d0',
        display: 'flex', alignItems: 'center', gap: 12,
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)',
    },
    vendorAvatar: {
        width: 42, height: 42, borderRadius: 12,
        background: 'linear-gradient(135deg,#10b981,#059669)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 900, fontSize: 18, flexShrink: 0,
        boxShadow: '0 4px 10px rgba(16,185,129,0.2)',
    },
    vendorInfo: { flex: 1, minWidth: 0 },
    vendorName: { fontSize: 14, fontWeight: 800, color: '#064e3b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    vendorBadge: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 11, color: '#059669', fontWeight: 700 },
    dot: { width: 6, height: 6, borderRadius: '50%', display: 'inline-block' },

    nav: { flex: 1, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' },
    navLabel: { fontSize: 10, fontWeight: 800, color: '#047857', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '18px 14px 8px' },
    navLabel2: { flex: 1, fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' },
    navBtn: {
        display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
        borderRadius: 14, border: 'none', background: 'transparent', cursor: 'pointer',
        color: '#065f46', fontFamily: FONT, width: '100%', textAlign: 'left',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative',
    },
    navBtnActive: {
        background: '#dcfce7', color: '#064e3b', boxShadow: '0 4px 12px rgba(22,163,74,0.08)',
    },
    navIcon: { display: 'flex', alignItems: 'center', padding: '8px', borderRadius: 10, background: '#ffffff', border: '1px solid #dcfce7', flexShrink: 0, transition: 'all 0.2s' },
    navIconActive: { background: '#ffffff', border: '1px solid #bbf7d0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
    navPip: {
        position: 'absolute', right: 12, width: 6, height: 6, borderRadius: '50%',
        background: '#10b981', boxShadow: '0 0 8px rgba(16,185,129,0.8)',
    },

    foot: {
        padding: '18px 20px', borderTop: '1px solid #dcfce7', display: 'flex',
        flexDirection: 'column', gap: 12,
    },
    footEmail: {
        fontSize: 12, color: '#047857', fontWeight: 600,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 4,
    },
    logoutBtn: {
        display: 'flex', alignItems: 'center', gap: 10,
        background: '#ffffff', border: '1px solid #fca5a5', borderRadius: 14,
        color: '#e11d48', fontWeight: 800, fontSize: 13, cursor: 'pointer',
        padding: '12px 16px', fontFamily: FONT, width: '100%',
        transition: 'all 0.2s',
    },
};


/* ─────────────────────── PAGES ─────────────────────── */

/* Overview */
function OverviewPage({ vendor, fleet, drivers, docs, contracts, pricing, trips, invoices, quotations, operatorDetails, onNavigate }) {
    const pendingTrips = trips.filter(t => t.vendorStatus === 'Pending Vendor' || t.vendorStatus === 'Not Assigned');
    const acceptedTrips = trips.filter(t => t.vendorStatus === 'Accepted');
    const proposedTrips = trips.filter(t => t.vendorStatus === 'Vendor Proposed');
    const rejectedTrips = trips.filter(t => t.vendorStatus === 'Rejected');
    
    const scheduledCount = trips.filter(t => t.vendorStatus === 'Accepted' && (t.status === 'Scheduled' || t.status === 'Requested')).length;
    const ongoingCount = trips.filter(t => t.status === 'Ongoing' || t.status === 'In Progress').length;
    const completedCount = trips.filter(t => t.status === 'Completed').length;

    const recentTrips = [...trips].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);
    const totalRevenue = invoices.reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);

    const unifiedKpis = [
        { label: 'Total Revenue', value: formatCurrency(totalRevenue, operatorDetails?.country), icon: icons.pricing || icons.dashboard },
        { label: 'Active Fleet', value: fleet?.length || 0, icon: icons.fleet || icons.dashboard },
        { label: 'Pending Actions', value: pendingTrips.length, icon: icons.alertCircle || icons.dashboard },
        { label: 'Scheduled Trips', value: scheduledCount, icon: icons.check || icons.dashboard },
        { label: 'Completed Trips', value: completedCount, icon: icons.check || icons.dashboard },
        { label: 'Active Contracts', value: contracts.length, icon: icons.contracts || icons.dashboard },
    ];

    const chartData = [
        { label: 'Pending', count: pendingTrips.length, color: '#f59e0b' },
        { label: 'Scheduled', count: scheduledCount, color: '#059669' },
        { label: 'Completed', count: completedCount, color: '#0ea5e9' },
        { label: 'Rejected', count: rejectedTrips.length, color: '#ef4444' },
    ].filter(d => d.count > 0);
    
    const totalChartCount = chartData.reduce((sum, d) => sum + d.count, 0) || 1;

    let cumulativePercent = 0;
    const getCoordinatesForPercent = (percent) => {
        const x = Math.cos(2 * Math.PI * percent);
        const y = Math.sin(2 * Math.PI * percent);
        return [x, y];
    };

    return (
        <div style={{ flex: 1, padding: '32px 40px', fontFamily: FONT, background: '#f8fafc', minHeight: '100vh', overflowY: 'auto' }}>
            
            {/* Minimal Header */}
            <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <h1 style={{ fontSize: 32, fontWeight: 800, color: '#0f172a', margin: '0 0 8px 0', letterSpacing: '-0.02em' }}>
                        Dashboard Overview
                    </h1>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 15 }}>
                        Welcome back, <span style={{ fontWeight: 600, color: '#0f172a' }}>{vendor?.vendorName}</span>
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    {vendor?.rating && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fef9c3', padding: '10px 16px', borderRadius: 8, border: '1px solid #fde047', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#eab308" stroke="#ca8a04" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                            </svg>
                            <span style={{ fontSize: 13, fontWeight: 800, color: '#854d0e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Rating: {vendor.rating}
                            </span>
                        </div>
                    )}
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', background: 'white', padding: '10px 20px', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                </div>
            </div>

            {/* Unified KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 32 }}>
                {unifiedKpis.map((kpi, idx) => (
                    <div key={idx} style={{ 
                        background: 'white', 
                        padding: '24px', 
                        borderRadius: 12, 
                        border: '1px solid #e2e8f0', 
                        borderLeft: '4px solid #1e3a8a',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 12
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {kpi.label}
                            </div>
                            <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: 8 }}>
                                <Icon d={kpi.icon} size={18} color="#1e3a8a" />
                            </div>
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>
                            {kpi.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Advanced Analytics Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 24 }}>
                
                {/* Revenue Analytics (Bar Chart) - Full Width or 2 columns */}
                <div style={{ gridColumn: 'span 2', background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '24px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 24 }}>Revenue Trend {invoices.length === 0 && <span style={{ fontSize: 11, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, marginLeft: 8 }}>DEMO DATA</span>}</div>
                    <div style={{ flex: 1, position: 'relative', height: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                        {(() => {
                            const revData = invoices.length > 0 ? [12, 19, 15, 22, 18, (totalRevenue/1000) || 25] : [5, 8, 6.5, 11, 9, 14];
                            const maxRev = Math.max(...revData) || 1;
                            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
                            
                            // Map values to 0-100 percentages for perfectly responsive absolute positioning
                            const pointsArray = revData.map((val, idx) => {
                                const xPct = (idx / 5) * 100;
                                const yPct = 100 - ((val / maxRev) * 100);
                                return { xPct, yPct };
                            });
                            
                            const pointsStr = pointsArray.map(p => `${p.xPct},${p.yPct}`).join(' ');
                            const areaStr = `${pointsStr} 100,100 0,100`;

                            return (
                                <div style={{ position: 'relative', width: '100%', height: '100%', padding: '15px 15px 35px' }}>
                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                        {/* Stretching Line */}
                                        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                                            <polyline points={pointsStr} fill="none" stroke="#2563eb" strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        
                                        {/* HTML Data Points (Stays Perfectly Round) */}
                                        {pointsArray.map((p, i) => (
                                            <div key={i} style={{ position: 'absolute', left: `${p.xPct}%`, top: `${p.yPct}%`, width: 12, height: 12, background: 'white', border: '2.5px solid #2563eb', borderRadius: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
                                        ))}

                                        {/* Perfectly Aligned Labels */}
                                        {months.map((m, i) => (
                                            <div key={`lbl-${i}`} style={{ position: 'absolute', bottom: -30, left: `${(i / 5) * 100}%`, fontSize: 12, fontWeight: 700, color: '#64748b', transform: 'translateX(-50%)' }}>
                                                {m}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* Fleet Utilization (Donut Chart) */}
                <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '24px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 24 }}>Fleet Utilization {fleet.length === 0 && <span style={{ fontSize: 11, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, marginLeft: 8 }}>DEMO DATA</span>}</div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        {(() => {
                            const fData = fleet.length > 0 
                                ? [{ l: 'Active', c: fleet.filter(f=>f.status==='Active').length, col: '#10b981' }, { l: 'Maint', c: fleet.filter(f=>f.status==='Under Maintenance').length, col: '#f59e0b' }, { l: 'Other', c: fleet.filter(f=>f.status!=='Active'&&f.status!=='Under Maintenance').length, col: '#94a3b8' }]
                                : [{ l: 'Active', c: 12, col: '#10b981' }, { l: 'Maint', c: 3, col: '#f59e0b' }, { l: 'Other', c: 1, col: '#94a3b8' }];
                            const tCount = fData.reduce((s, d) => s + d.c, 0) || 1;
                            let cPct = 0;
                            return (
                                <>
                                    <svg viewBox="-1.2 -1.2 2.4 2.4" style={{ width: 140, height: 140, transform: 'rotate(-90deg)' }}>
                                        {fData.map((d, i) => {
                                            if (d.c === 0) return null;
                                            const pct = d.c / tCount;
                                            const [sx, sy] = [Math.cos(2*Math.PI*cPct), Math.sin(2*Math.PI*cPct)];
                                            cPct += pct;
                                            const [ex, ey] = [Math.cos(2*Math.PI*cPct), Math.sin(2*Math.PI*cPct)];
                                            const large = pct > 0.5 ? 1 : 0;
                                            if (pct === 1) return <circle key={i} cx="0" cy="0" r="1" fill="none" stroke={d.col} strokeWidth="0.3" />;
                                            return <path key={i} d={`M ${sx} ${sy} A 1 1 0 ${large} 1 ${ex} ${ey}`} fill="none" stroke={d.col} strokeWidth="0.3" />;
                                        })}
                                    </svg>
                                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                        <div style={{ fontSize: 24, fontWeight: 900, color: '#0f172a' }}>{Math.round((fData[0].c / tCount) * 100)}%</div>
                                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>ACTIVE</div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>

                {/* Trip Distribution */}
                <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '24px' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 24 }}>Trip Distribution {chartData.length === 0 && <span style={{ fontSize: 11, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, marginLeft: 8 }}>DEMO DATA</span>}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        {(() => {
                            const cData = chartData.length > 0 ? chartData : [ { label: 'Completed', count: 45, color: '#0ea5e9' }, { label: 'Scheduled', count: 15, color: '#059669' }, { label: 'Pending', count: 5, color: '#f59e0b' } ];
                            const tc = cData.reduce((s, d) => s + d.count, 0) || 1;
                            let cPct = 0;
                            return (
                                <>
                                    <svg viewBox="-1.2 -1.2 2.4 2.4" style={{ width: 120, height: 120, transform: 'rotate(-90deg)', flexShrink: 0 }}>
                                        {cData.map((d, i) => {
                                            const pct = d.count / tc;
                                            const [sx, sy] = [Math.cos(2*Math.PI*cPct), Math.sin(2*Math.PI*cPct)];
                                            cPct += pct;
                                            const [ex, ey] = [Math.cos(2*Math.PI*cPct), Math.sin(2*Math.PI*cPct)];
                                            const large = pct > 0.5 ? 1 : 0;
                                            if (pct === 1) return <circle key={i} cx="0" cy="0" r="1" fill={d.color} />;
                                            return <path key={i} d={`M 0 0 L ${sx} ${sy} A 1 1 0 ${large} 1 ${ex} ${ey} Z`} fill={d.color} />;
                                        })}
                                    </svg>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {cData.map(d => (
                                            <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: d.color }}></div>
                                                <div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{d.count}</div>
                                                    <div style={{ fontSize: 11, color: '#64748b' }}>{d.label}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>

                {/* Compliance & Documents */}
                <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '24px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 24 }}>Compliance Gauge {docs.length === 0 && <span style={{ fontSize: 11, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, marginLeft: 8 }}>DEMO DATA</span>}</div>
                    {(() => {
                        const valid = docs.filter(d => d.status === 'Valid').length;
                        const pct = docs.length > 0 ? Math.round((valid / docs.length) * 100) : 85;
                        return (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                    <span>Verified Documents</span>
                                    <span style={{ color: pct >= 80 ? '#10b981' : '#f59e0b' }}>{pct}%</span>
                                </div>
                                <div style={{ width: '100%', height: 12, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: pct >= 80 ? '#10b981' : '#f59e0b', borderRadius: 6 }}></div>
                                </div>
                                <div style={{ marginTop: 24, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Icon d={icons.docs} size={20} color="#64748b" />
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{docs.length > 0 ? docs.length : 12} Total Files</div>
                                        <div style={{ fontSize: 12, color: '#64748b' }}>Securely stored</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Recent Trips Feed */}
                <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Recent Trips</div>
                        <button onClick={() => onNavigate('All')} style={{ fontSize: 13, fontWeight: 600, color: '#1e3a8a', background: 'none', border: 'none', cursor: 'pointer' }}>View All &rarr;</button>
                    </div>
                    <div style={{ overflowY: 'auto', flex: 1, padding: '0 24px', maxHeight: 200 }}>
                        {recentTrips.length === 0 ? (
                            <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No recent trips.</div>
                        ) : recentTrips.map((t, i) => (
                            <div key={t._id} style={{ padding: '16px 0', borderBottom: i === recentTrips.length - 1 ? 'none' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                                        {t.pickupLocation || 'Pickup'} &rarr; {t.dropLocation || 'Drop'}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                        {t.tripDate ? new Date(t.tripDate).toLocaleDateString() : 'Multi-day'} &middot; {t.clientId?.companyName || 'Corporate Client'}
                                    </div>
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: '#f1f5f9', color: '#475569' }}>
                                    {t.vendorStatus || 'Pending'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Profile Block */}
            <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Organizational Profile</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    {[
                        { label: 'Entity Name', value: vendor?.vendorName, icon: icons.building || icons.dashboard },
                        { label: 'Authorized Person', value: vendor?.contactPerson || '—', icon: icons.profile || icons.dashboard },
                        { label: 'Contact Email', value: vendor?.email, icon: icons.mail || icons.dashboard },
                        { label: 'Operational Phone', value: vendor?.phone || '—', icon: icons.phone || icons.dashboard },
                        { label: 'Business Category', value: vendor?.category?.name || 'Logistics Partner', icon: icons.tag || icons.dashboard },
                        { label: 'Tax Identification', value: vendor?.taxId || '—', icon: icons.tag || icons.dashboard },
                        { label: 'Global Address', value: vendor?.address || '—', icon: icons.pin || icons.dashboard, span: 2 },
                    ].map((f, idx) => (
                        <div key={idx} style={{ 
                            padding: '20px 24px', 
                            borderRight: (idx + 1) % 4 === 0 ? 'none' : '1px solid #f1f5f9', 
                            borderBottom: idx < 4 ? '1px solid #f1f5f9' : 'none',
                            gridColumn: f.span ? `span ${f.span}` : 'auto'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                <Icon d={f.icon} size={14} color="#64748b" />
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{f.label}</span>
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{f.value}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}





/* Generic table page */
function TablePage({ title, icon, columns, rows, emptyMsg, onAdd, onEdit, onDelete, kpis }) {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredRows = rows.filter(row => {
        if (!searchTerm) return true;
        return columns.some(c => {
            const val = row[c.key];
            if (val == null) return false;
            return String(val).toLowerCase().includes(searchTerm.toLowerCase());
        });
    });

    return (
        <div style={pg.root}>
            <div style={pg.header}>
                <div>
                    <h1 style={pg.title}>{title}</h1>
                    <p style={pg.sub}>{filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''} found</p>
                </div>
                {onAdd && (
                    <button onClick={onAdd} style={pg.addBtn}>
                        <Icon d={icons.plus} size={15} color="white" /> Add New
                    </button>
                )}
            </div>

            {/* Uniform KPIs */}
            {kpis && kpis.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, marginBottom: 32 }}>
                    {kpis.map((kpi, idx) => (
                        <div key={idx} style={{ 
                            background: 'white', 
                            padding: '24px', 
                            borderRadius: 12, 
                            border: '1px solid #e2e8f0', 
                            borderLeft: '4px solid #1e3a8a',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 12
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {kpi.label}
                                </div>
                                <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: 8 }}>
                                    <Icon d={kpi.icon} size={18} color="#1e3a8a" />
                                </div>
                            </div>
                            <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>
                                {kpi.value}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div style={pg.section}>
                <div style={{ ...pg.sectionHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <Icon d={icon} size={17} color="#059669" /> {title}
                    </div>
                    <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: 12, top: 9 }}>
                            <Icon d={icons.search} size={14} color="#94a3b8" />
                        </div>
                        <input 
                            type="text" 
                            placeholder={`Search ${title.toLowerCase()}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ padding: '7px 12px 7px 32px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, width: 220, outline: 'none', background: '#f8fafc' }}
                        />
                    </div>
                </div>
                {filteredRows.length === 0 ? (
                    <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                        {searchTerm ? 'No matching records found for your search.' : emptyMsg}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={pg.table}>
                            <thead>
                                <tr>
                                    {columns.map(c => <th key={c.key} style={pg.th}>{c.label}</th>)}
                                    {(onEdit || onDelete) && <th style={{ ...pg.th, textAlign: 'right' }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((row, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                        {columns.map(c => (
                                            <td key={c.key} style={pg.td}>
                                                {c.render ? c.render(row[c.key], row) : (row[c.key] || '—')}
                                            </td>
                                        ))}
                                        {(onEdit || onDelete) && (
                                            <td style={{ ...pg.td, textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                    {onEdit && (
                                                        <button onClick={() => onEdit(row)} style={pg.actBtn}>
                                                            <Icon d={icons.edit} size={14} color="#64748b" />
                                                        </button>
                                                    )}
                                                    {onDelete && (
                                                        <button onClick={() => onDelete(row)} style={{ ...pg.actBtn, ':hover': { background: '#fee2e2' } }}>
                                                            <Icon d={icons.trash} size={14} color="#ef4444" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

/* Profile */
function ProfilePage({ vendor, onUpdate }) {
    const [edit, setEdit] = useState(false);
    const [form, setForm] = useState({
        contactPerson: vendor?.contactPerson || '',
        phone: vendor?.phone || '',
        address: vendor?.address || '',
        password: vendor?.password || ''
    });

    const handleSave = async (e) => {
        e.preventDefault();
        onUpdate(form);
        setEdit(false);
    };

    return (
        <div style={pg.root}>
            <div style={pg.header}>
                <h1 style={pg.title}>Manage Profile</h1>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => setEdit(!edit)} style={{ ...pg.addBtn, background: edit ? '#94a3b8' : '#10b981' }}>
                        {edit ? 'Cancel' : 'Edit Profile'}
                    </button>
                </div>
            </div>
            <div style={pg.section}>
                <div style={pg.sectionHeader}><Icon d={icons.profile} size={17} color="#059669" /> Account Information</div>
                <div style={{ padding: 24 }}>
                    {!edit ? (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 28, paddingBottom: 24, borderBottom: '1px solid #f3f4f6' }}>
                                <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 900, fontSize: 32, boxShadow: '0 8px 24px rgba(16,185,129,0.3)' }}>
                                    {vendor?.vendorName?.charAt(0)?.toUpperCase()}
                                </div>
                                <div>
                                    <div style={{ fontSize: 22, fontWeight: 800, color: '#064e3b' }}>{vendor?.vendorName}</div>
                                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{vendor?.category?.name || 'Vendor'}</div>
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '3px 10px', background: '#ecfdf5', borderRadius: 20, fontSize: 12, fontWeight: 700, color: '#065f46', border: '1px solid #6ee7b7' }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                                        {vendor?.status || 'Active'}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 20 }}>
                                {[
                                    { label: 'Contact Person', value: vendor?.contactPerson, icon: icons.profile },
                                    { label: 'Email', value: vendor?.email, icon: icons.mail },
                                    { label: 'Phone', value: vendor?.phone, icon: icons.phone },
                                    { label: 'Tax ID', value: vendor?.taxId, icon: icons.tag },
                                    { label: 'Address', value: vendor?.address, icon: icons.pin },
                                ].filter(f => f.label).map(f => (
                                    <div key={f.label} style={{ padding: '14px 16px', background: '#f9fafb', borderRadius: 12, border: '1px solid #f3f4f6' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                                            <Icon d={f.icon} size={14} color="#10b981" />
                                            <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{f.label}</span>
                                        </div>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{f.value || '—'}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <form onSubmit={handleSave} style={{ display: 'grid', gap: 16 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div style={formStyle.group}>
                                    <label style={formStyle.label}>Contact Person</label>
                                    <input style={formStyle.input} value={form.contactPerson} onChange={e => setForm({ ...form, contactPerson: e.target.value })} />
                                </div>
                                <div style={formStyle.group}>
                                    <label style={formStyle.label}>Phone Number</label>
                                    <input style={formStyle.input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                                </div>
                            </div>
                            <div style={formStyle.group}>
                                <label style={formStyle.label}>Address</label>
                                <input style={formStyle.input} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
                            </div>
                            <div style={formStyle.group}>
                                <label style={formStyle.label}>Update Password</label>
                                <PasswordInput style={formStyle.input} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Leave blank to keep current" />
                            </div>
                            <button type="submit" style={formStyle.submit}>Save Profile Changes</button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

const formStyle = {
    group: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.03em' },
    input: { padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none' },
    submit: { background: '#10b981', color: 'white', border: 'none', padding: '12px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginTop: 10 }
};

const pg = {
    root: { flex: 1, padding: '36px 44px', fontFamily: FONT, background: '#FDFBF7', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 },
    title: { fontSize: 26, fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.03em' },
    sub: { fontSize: 14, color: '#64748b', margin: '7px 0 0', fontWeight: 500 },
    statusPill: { display: 'flex', alignItems: 'center', gap: 7, padding: '7px 16px', background: '#ecfdf5', borderRadius: 24, fontSize: 12.5, fontWeight: 700, color: '#065f46', border: '1px solid #a7f3d0' },
    addBtn: { background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 16px rgba(16,185,129,0.3)', letterSpacing: '-0.01em' },
    actBtn: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    statCard: { padding: '22px 22px 18px', borderRadius: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)' },
    section: { background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' },
    sectionHeader: { display: 'flex', alignItems: 'center', gap: 9, padding: '16px 22px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 800, color: '#0f172a', background: '#fafbfc' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '13px 18px', fontSize: 10.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '15px 18px', fontSize: 13.5, color: '#334155', fontWeight: 500, borderBottom: '1px solid #f1f5f9' },
};

/* ─────────────────────── MODAL ───────────────────────── */
function Modal({ title, children, onClose, onSave, loading, maxWidth }) {
    return (
        <div style={md.overlay} onClick={onClose}>
            <div style={{ ...md.content, maxWidth: maxWidth || 520 }} onClick={e => e.stopPropagation()}>
                <div style={md.header}>
                    <div style={md.title}>{title}</div>
                    <button onClick={onClose} style={md.close}><Icon d={icons.x} size={20} color="#94a3b8" /></button>
                </div>
                <div style={md.body}>{children}</div>
                <div style={md.footer}>
                    <button onClick={onClose} style={md.cancelBtn}>Cancel</button>
                    <button onClick={onSave} disabled={loading} style={md.saveBtn}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const md = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    content: { background: 'white', borderRadius: 22, width: '100%', maxWidth: 520, boxShadow: '0 32px 64px -12px rgba(0,0,0,0.35)', fontFamily: FONT },
    header: { padding: '22px 26px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    title: { fontSize: 19, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' },
    close: { background: '#f1f5f9', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 9, display: 'flex' },
    body: { padding: '22px 26px', maxHeight: '68vh', overflowY: 'auto' },
    footer: { padding: '16px 26px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 },
    cancelBtn: { padding: '10px 22px', border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 11, fontSize: 13.5, fontWeight: 600, color: '#64748b', cursor: 'pointer', fontFamily: FONT },
    saveBtn: { padding: '10px 26px', border: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', borderRadius: 11, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(16,185,129,0.3)', fontFamily: FONT },
};

/* ─────────────────────── VENDOR TRIPS PAGE ─────────────────────── */
function VendorTripsPage({ trips, fleet, drivers, vendorId, operatorId, operatorDetails, onRefresh, showToast, initialFilter, hasActiveContract }) {
    const [respondModal, setRespondModal] = useState(null); // trip being responded to
    const [proposal, setProposal] = useState({ vehicleId: '', driverId: '', amount: '' });
    const [submitting, setSubmitting] = useState(false);

    const [filter, setFilter] = useState(initialFilter || 'All');

    useEffect(() => {
        if (initialFilter) setFilter(initialFilter);
    }, [initialFilter]);

    const statusConfig = {
        'Not Assigned': { bg: '#f3f4f6', color: '#6b7280', label: 'Not Assigned' },
        'Pending Vendor': { bg: '#fef9c3', color: '#92400e', label: 'Awaiting Your Response' },
        'Vendor Proposed': { bg: '#dbeafe', color: '#1e40af', label: 'Proposal Sent' },
        'Accepted': { bg: '#d1fae5', color: '#065f46', label: 'Scheduled' },
        'Rejected': { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
    };

    const openRespond = (trip) => {
        setProposal({ vehicleId: '', driverId: '', amount: trip.vendorPrice || trip.vendorProposedAmount || '' });
        setRespondModal(trip);
    };

    const submitProposal = async () => {
        if (!proposal.vehicleId || !proposal.driverId || !proposal.amount) {
            showToast('Please select fleet, driver and enter amount', 'error');
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`/api/trips/${respondModal._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    vendorProposedVehicleId: proposal.vehicleId,
                    vendorProposedDriverId: proposal.driverId,
                    vendorProposedAmount: Number(proposal.amount),
                    vendorStatus: 'Vendor Proposed',
                }),
            });
            if (!res.ok) throw new Error('Failed to submit proposal');
            showToast('Proposal submitted successfully! Waiting for operator confirmation.');
            setRespondModal(null);
            onRefresh();
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredTrips = trips.filter(t => {
        if (filter === 'All') return true;
        if (filter === 'Scheduled') return t.vendorStatus === 'Accepted' && (t.status === 'Scheduled' || t.status === 'Requested');
        if (filter === 'Ongoing') return t.status === 'Ongoing' || t.status === 'In Progress';
        return t.status === filter;
    });

    const pendingTrips = filteredTrips.filter(t => t.vendorStatus === 'Pending Vendor' || t.vendorStatus === 'Not Assigned');
    const otherTrips = filteredTrips.filter(t => t.vendorStatus !== 'Pending Vendor' && t.vendorStatus !== 'Not Assigned');

    const kpis = [
        { label: 'Total Trips', value: trips.length, icon: icons.trips },
        { label: 'Action Required', value: trips.filter(t => t.vendorStatus === 'Pending Vendor').length, icon: icons.alertCircle },
        { label: 'Scheduled', value: trips.filter(t => t.vendorStatus === 'Accepted').length, icon: icons.check },
        { label: 'Rejected', value: trips.filter(t => t.vendorStatus === 'Rejected').length, icon: icons.analytics },
    ];

    return (
        <div style={pg.root}>
            <div style={pg.header}>
                <div>
                    <h1 style={pg.title}>My Trips</h1>
                    <p style={pg.sub}>{filteredTrips.length} trip{filteredTrips.length !== 1 ? 's' : ''} {filter !== 'All' ? `(${filter})` : ''} assigned to your account</p>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', padding: '6px', borderRadius: 16, border: '1px solid #e2e8f0' }}>
                        {['All', 'Scheduled', 'Ongoing', 'Completed'].map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                style={{
                                    padding: '8px 18px', borderRadius: 12, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                    background: filter === f ? 'white' : 'transparent',
                                    color: filter === f ? '#059669' : '#64748b',
                                    boxShadow: filter === f ? '0 4px 6px -1px rgba(0,0,0,0.05)' : 'none',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                    {pendingTrips.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#fef9c3', border: '1.5px solid #fcd34d', borderRadius: 10 }}>
                            <Icon d={icons.alertCircle} size={16} color="#92400e" />
                            <span style={{ fontSize: 13, fontWeight: 800, color: '#92400e' }}>{pendingTrips.length} Action Needed</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Uniform KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, marginBottom: 32 }}>
                {kpis.map((kpi, idx) => (
                    <div key={idx} style={{ 
                        background: 'white', 
                        padding: '24px', 
                        borderRadius: 12, 
                        border: '1px solid #e2e8f0', 
                        borderLeft: '4px solid #1e3a8a',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 12
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {kpi.label}
                            </div>
                            <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: 8 }}>
                                <Icon d={kpi.icon} size={18} color="#1e3a8a" />
                            </div>
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>
                            {kpi.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Pending trips — action required */}
            {pendingTrips.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', animation: 'pulse 2s infinite' }}></div>
                        High Priority Response Required
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {pendingTrips.map(t => (
                            <div key={t._id} style={{ 
                                background: 'white', 
                                borderRadius: 24, 
                                border: '1px solid #fde68a', 
                                boxShadow: '0 10px 20px -5px rgba(217,119,6,0.06)', 
                                padding: '28px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 32, 
                                flexWrap: 'wrap',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{ position: 'absolute', top: 0, right: 0, width: 100, height: 100, background: 'linear-gradient(135deg, transparent 50%, #fef3c7 100%)', opacity: 0.5 }}></div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                        <span style={{ padding: '4px 14px', borderRadius: 20, fontSize: 10, fontWeight: 800, background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Awaiting Proposal</span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                                        <div>
                                            <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Route Configuration</div>
                                            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{t.pickupLocation}</div>
                                            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>to {t.dropLocation}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Schedule & Date</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#334155' }}>{t.tripDate ? new Date(t.tripDate).toLocaleDateString(undefined, { dateStyle: 'medium' }) : t.recurringDays?.join(', ') || '—'}</div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t.tripType} · {t.tripFrequency}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Corporate Partner</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#334155' }}>{t.clientId?.companyName || 'Standard Client'}</div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Operational Assignment</div>
                                        </div>
                                    </div>
                                </div>
                                {hasActiveContract ? (
                                    <button
                                        onClick={() => openRespond(t)}
                                        style={{ 
                                            background: 'linear-gradient(135deg,#10b981,#059669)', 
                                            color: 'white', border: 'none', borderRadius: 16, 
                                            padding: '16px 32px', fontWeight: 800, fontSize: 15, 
                                            cursor: 'pointer', boxShadow: '0 8px 20px rgba(16,185,129,0.25)', 
                                            display: 'flex', alignItems: 'center', gap: 10, 
                                            whiteSpace: 'nowrap', transition: 'all 0.2s',
                                            zIndex: 2
                                        }}
                                        className="respond-btn-premium"
                                    >
                                        <style>{`.respond-btn-premium:hover { transform: scale(1.02); filter: brightness(1.05); }`}</style>
                                        <Icon d={icons.send} size={18} color="white" /> Draft Proposal
                                    </button>
                                ) : (
                                    <div style={{
                                        background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 16,
                                        padding: '12px 24px', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, zIndex: 2
                                    }}>
                                        <Icon d={icons.alertCircle} size={16} /> Action disabled: Contract inactive
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Other trips */}
            {otherTrips.length > 0 && (
                <div style={pg.section}>
                    <div style={pg.sectionHeader}><Icon d={icons.trip} size={17} color="#059669" /> All Trips</div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={pg.table}>
                            <thead>
                                <tr>
                                    {['Date', 'Route', 'Type', 'Client', 'Your Fleet', 'Your Driver', 'Amount', 'Status'].map(h => (
                                        <th key={h} style={pg.th}>{h}</th>
                                    ))}
                                    <th style={{ ...pg.th, textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {otherTrips.map((t, i) => {
                                    const sc = statusConfig[t.vendorStatus] || statusConfig['Not Assigned'];
                                    return (
                                        <tr key={t._id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                            <td style={pg.td}>{t.tripDate ? new Date(t.tripDate).toLocaleDateString() : t.recurringDays?.join(', ') || '—'}</td>
                                            <td style={pg.td}>
                                                <div style={{ fontWeight: 600 }}>{t.pickupLocation}</div>
                                                <div style={{ fontSize: 11, color: '#6b7280' }}>→ {t.dropLocation}</div>
                                            </td>
                                            <td style={pg.td}>{t.tripType}</td>
                                            <td style={pg.td}>{t.clientId?.companyName || '—'}</td>
                                            <td style={pg.td}>{t.vendorProposedVehicleId?.registrationNumber || '—'}</td>
                                            <td style={pg.td}>{t.vendorProposedDriverId?.name || '—'}</td>
                                            <td style={pg.td}>{t.vendorPrice || t.vendorProposedAmount ? formatCurrency(t.vendorPrice || t.vendorProposedAmount, operatorDetails?.country) : '—'}</td>
                                            <td style={pg.td}>
                                                <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color }}>
                                                    {sc.label}
                                                </span>
                                            </td>
                                            <td style={{ ...pg.td, textAlign: 'right' }}>
                                                {t.vendorStatus === 'Rejected' && hasActiveContract && (
                                                    <button onClick={() => openRespond(t)} style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>Resubmit</button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {trips.length === 0 && (
                <div style={{ ...pg.section, padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                    No trips assigned to your account yet. You will be notified when an operator assigns a trip to you.
                </div>
            )}

            {/* Respond Modal */}
            {respondModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 520, boxShadow: '0 25px 50px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#ecfdf5,#f0fdf4)' }}>
                            <div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: '#064e3b' }}>Respond to Trip</div>
                                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{respondModal.pickupLocation} → {respondModal.dropLocation}</div>
                            </div>
                            <button onClick={() => setRespondModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Icon d={icons.x} size={22} color="#6b7280" /></button>
                        </div>
                        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div style={formStyle.group}>
                                <label style={formStyle.label}>Assign Fleet Vehicle *</label>
                                <select style={{ ...formStyle.input, height: 44 }} value={proposal.vehicleId} onChange={e => setProposal({ ...proposal, vehicleId: e.target.value })}>
                                    <option value="">Select Vehicle</option>
                                    {fleet.map(v => <option key={v._id} value={v._id}>{v.registrationNumber} — {v.vehicleType} {v.make} {v.model}</option>)}
                                </select>
                            </div>
                            <div style={formStyle.group}>
                                <label style={formStyle.label}>Assign Driver *</label>
                                <select style={{ ...formStyle.input, height: 44 }} value={proposal.driverId} onChange={e => setProposal({ ...proposal, driverId: e.target.value })}>
                                    <option value="">Select Driver</option>
                                    {drivers.map(d => <option key={d._id} value={d._id}>{d.name} {d.phoneNumber ? `· ${d.phoneNumber}` : ''}</option>)}
                                </select>
                            </div>
                            <div style={formStyle.group}>
                                <label style={formStyle.label}>Your Price / Amount *</label>
                                <input type="number" placeholder="Enter amount" style={{ ...formStyle.input, height: 44 }} value={proposal.amount} onChange={e => setProposal({ ...proposal, amount: e.target.value })} />
                            </div>
                            <div style={{ background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', padding: 12, fontSize: 12, color: '#065f46', lineHeight: 1.5 }}>
                                💡 Once submitted, the operator will review your proposal and either accept or reject it. You'll be notified of their decision.
                            </div>
                        </div>
                        <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button onClick={() => setRespondModal(null)} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', background: 'white', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#64748b', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={submitProposal} disabled={submitting} style={{ padding: '10px 24px', border: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 12px rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Icon d={icons.send} size={14} color="white" />
                                {submitting ? 'Submitting...' : 'Submit Proposal'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─────────────────────── MAIN PORTAL ─────────────────────── */
export default function VendorPortal({ vendor: initialVendor, onLogout, operatorDetails }) {
    const [activePage, setActivePage] = useState('overview');
    const [tripStatusFilter, setTripStatusFilter] = useState('All');
    const [vendorData, setVendorData] = useState(initialVendor); // fresh copy with rating/remarks
    const [fleet, setFleet] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [docs, setDocs] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [pricing, setPricing] = useState([]);
    const [trips, setTrips] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [masterData, setMasterData] = useState({});
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);

    const [modal, setModal] = useState({ show: false, type: '', data: null });
    const [documentsList, setDocumentsList] = useState([{ name: '', file: null }]);
    const [driverEditData, setDriverEditData] = useState(null);
    const [fleetEditData, setFleetEditData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    const vendorId = initialVendor?._id;
    const operatorId = initialVendor?.operatorId;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleAddDocument = () => setDocumentsList(prev => [...prev, { name: '', file: null }]);
    
    const handleDocumentChange = (index, field, value) => {
        const updated = [...documentsList];
        updated[index][field] = value;
        setDocumentsList(updated);
    };

    const handleRemoveDocument = (index) => {
        const updated = [...documentsList];
        updated.splice(index, 1);
        setDocumentsList(updated);
    };

    const loadData = useCallback(async () => {
        if (!vendorId || !operatorId) return;
        setLoading(true);
        try {
            const [fleetRes, driversRes, docsRes, contractsRes, pricingRes, tripsRes, invoicesRes, quotesRes, masterRes, notifRes, vendorsRes] = await Promise.all([
                fetch(`${API}/vendor-fleet/${operatorId}`),
                fetch(`${API}/drivers/${operatorId}`),
                fetch(`${API}/vendor-documents/${operatorId}`),
                fetch(`${API}/vendor-contracts/${operatorId}`),
                fetch(`${API}/vendor-pricing/${operatorId}`),
                fetch(`${API}/trips/${operatorId}`),
                fetch(`${API}/invoices/${operatorId}`),
                fetch(`${API}/quotations/${operatorId}`),
                fetch(`${API}/master-data`),
                fetch(`${API}/notifications/vendor/${vendorId}`),
                fetch(`${API}/vendors/${operatorId}`),
            ]);
            const [fleetData, driversData, docsData, contractsData, pricingData, tripsData, invoicesData, quotesData, mData, notifData, vendorsData] = await Promise.all([
                fleetRes.json(), driversRes.json(), docsRes.json(), contractsRes.json(), pricingRes.json(),
                tripsRes.json(), invoicesRes.json(), quotesRes.json(), masterRes.json(), notifRes.json(), vendorsRes.json()
            ]);
            // Pick fresh vendor record to get latest rating/remarks
            if (Array.isArray(vendorsData)) {
                const fresh = vendorsData.find(v => String(v._id) === String(vendorId));
                if (fresh) setVendorData(fresh);
            }

            // Group master data
            const masterMap = {};
            if (Array.isArray(mData)) {
                mData.forEach(m => {
                    if (!masterMap[m.category]) masterMap[m.category] = [];
                    masterMap[m.category].push(m);
                });
            }
            setMasterData(masterMap);

            setFleet(Array.isArray(fleetData) ? fleetData.filter(f => String(f.vendorId?._id || f.vendorId) === String(vendorId)) : []);
            setDrivers(Array.isArray(driversData) ? driversData.filter(d => String(d.vendorId?._id || d.vendorId) === String(vendorId)) : []);
            setDocs(Array.isArray(docsData) ? docsData.filter(d => String(d.vendorId?._id || d.vendorId) === String(vendorId)) : []);
            setContracts(Array.isArray(contractsData) ? contractsData.filter(c => String(c.vendorId?._id || c.vendorId) === String(vendorId)) : []);
            setPricing(Array.isArray(pricingData) ? pricingData.filter(p => String(p.vendorId?._id || p.vendorId) === String(vendorId)) : []);
            setTrips(Array.isArray(tripsData) ? tripsData.filter(t => String(t.vendorId?._id || t.vendorId) === String(vendorId)) : []);
            setInvoices(Array.isArray(invoicesData) ? invoicesData.filter(i => String(i.vendorId?._id || i.vendorId) === String(vendorId)) : []);
            setQuotations(Array.isArray(quotesData) ? quotesData.filter(q => String(q.vendorId?._id || q.vendorId) === String(vendorId)) : []);
            setNotifications(Array.isArray(notifData) ? notifData : []);
        } catch (err) {
            console.error('Vendor portal data load error:', err);
        } finally {
            setLoading(false);
        }
    }, [vendorId, operatorId]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleAction = (type, data = null) => {
        setModal({ show: true, type, data: data || getDefaultData(type) });
    };

    const getDefaultData = (type) => {
        switch (type) {
            case 'fleet': return { vehicleType: '', registrationNumber: '', make: '', model: '', year: new Date().getFullYear(), fuelType: '', color: '', purchasePrice: '', acquisitionDate: '', depreciationMonthly: '', gpsMonthly: '', dashcamMonthly: '', insuranceMonthly: '', otherCostsMonthly: '', status: 'Under Review' };
            case 'drivers': return { name: '', email: '', phoneNumber: '', status: 'Under Review' };
            case 'docs': return { documentType: '', description: '', expiryDate: '', status: 'Under Review' };
            case 'contracts': return { contractType: '', startDate: '', endDate: '', value: 0, status: 'Under Review' };
            case 'pricing': return { serviceType: '', basePrice: 0, perKmPrice: 0, surgePrice: 0, status: 'Active' };
            default: return {};
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const isEdit = modal.data._id;
            let url = '';
            let method = isEdit ? 'PUT' : 'POST';

            switch (modal.type) {
                case 'fleet': url = `/api/vendor-fleet${isEdit ? '/' + modal.data._id : ''}`; break;
                case 'drivers': url = `/api/drivers${isEdit ? '/' + modal.data._id : ''}`; break;
                case 'docs': url = `/api/vendor-documents${isEdit ? '/' + modal.data._id : ''}`; break;
                case 'contracts': url = `/api/vendor-contracts${isEdit ? '/' + modal.data._id : ''}`; break;
                case 'pricing': url = `/api/vendor-pricing${isEdit ? '/' + modal.data._id : ''}`; break;
                default: break;
            }

            const payload = { ...modal.data, vendorId, operatorId };
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Failed to save');
            showToast('Changes saved successfully');
            setModal({ show: false, type: '', data: null }); setDocumentsList([{ name: '', file: null }]);;
            loadData();
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (type, item) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        try {
            let url = '';
            switch (type) {
                case 'fleet': url = `/api/vendor-fleet/${item._id}`; break;
                case 'docs': url = `/api/vendor-documents/${item._id}`; break;
                case 'contracts': url = `/api/vendor-contracts/${item._id}`; break;
                case 'pricing': url = `/api/vendor-pricing/${item._id}`; break;
                default: break;
            }
            const res = await fetch(url, { method: 'DELETE' });
            if (!res.ok) throw new Error('Delete failed');
            showToast('Record deleted successfully');
            loadData();
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    const handleProfileUpdate = async (formData) => {
        try {
            const res = await fetch(`/api/vendors/${vendorId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error('Update failed');
            showToast('Profile updated successfully');
            // We should ideally update initialVendor here, but since it's a prop, 
            // the parent App should refresh if we want it perfect. 
            // For now, refreshing the page is a simple fallback or just trust the local state would be better.
            window.location.reload();
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    const renderPage = () => {
        if (loading) return (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', fontSize: 16, fontFamily: 'Inter,sans-serif' }}>
                Loading…
            </div>
        );
        switch (activePage) {
            case 'support':
                return <SupportTickets context="vendor" userDetails={vendorData} trips={trips} />;
            case 'overview':
                return <OverviewPage
                    vendor={vendorData}
                    fleet={fleet}
                    drivers={drivers}
                    docs={docs}
                    contracts={contracts}
                    pricing={pricing}
                    trips={trips}
                    invoices={invoices}
                    quotations={quotations}
                    operatorDetails={operatorDetails}
                    onNavigate={(status) => {
                        setTripStatusFilter(status);
                        setActivePage('trips');
                    }}
                />;
            case 'trips':
                return <VendorTripsPage
                    trips={trips}
                    fleet={fleet}
                    drivers={drivers}
                    vendorId={vendorId}
                    operatorId={operatorId}
                    operatorDetails={operatorDetails}
                    onRefresh={loadData}
                    showToast={showToast}
                    initialFilter={tripStatusFilter}
                    hasActiveContract={hasActiveContract}
                />;
            case 'invoices':
                return <TablePage
                    title="Vendor Invoices"
                    icon={icons.invoice}
                    emptyMsg="No invoices found."
                    kpis={[
                        { label: 'Total Invoiced', value: formatCurrency(invoices.reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0), operatorDetails?.country), icon: icons.invoice },
                        { label: 'Paid Invoices', value: invoices.filter(inv => inv.status === 'Paid').length, icon: icons.check },
                        { label: 'Processing', value: invoices.filter(inv => inv.status !== 'Paid').length, icon: icons.alertCircle },
                    ]}
                    columns={[
                        { key: 'invoiceNumber', label: 'Invoice #' },
                        { key: 'issueDate', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                        { key: 'dueDate', label: 'Due Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                        { key: 'totalAmount', label: 'Amount', render: v => v ? formatCurrency(Number(v), operatorDetails?.country) : '—' },
                        { key: 'status', label: 'Status' },
                    ]}
                    rows={invoices}
                />;
            case 'quotations':
                return <TablePage
                    title="Vendor Quotations"
                    icon={icons.quote}
                    emptyMsg="No quotations found."
                    kpis={[
                        { label: 'Total Quotes', value: quotations.length, icon: icons.quote },
                        { label: 'Approved', value: quotations.filter(q => q.status === 'Approved').length, icon: icons.check },
                        { label: 'Under Review', value: quotations.filter(q => q.status !== 'Approved' && q.status !== 'Rejected').length, icon: icons.analytics },
                    ]}
                    columns={[
                        { key: 'quotationNumber', label: 'Quote #' },
                        { key: 'issueDate', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                        { key: 'validUntil', label: 'Expiry', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                        { key: 'totalAmount', label: 'Amount', render: v => v ? formatCurrency(Number(v), operatorDetails?.country) : '—' },
                        { key: 'status', label: 'Status' },
                    ]}
                    rows={quotations}
                />;
            case 'fleet':
                return <TablePage
                    title="Manage Fleet"
                    icon={icons.fleet}
                    emptyMsg="No fleet vehicles assigned to your account yet."
                    kpis={[
                        { label: 'Total Vehicles', value: fleet.length, icon: icons.fleet },
                        { label: 'Active Fleet', value: fleet.filter(f => f.status === 'Active').length, icon: icons.check },
                        { label: 'Under Maintenance', value: fleet.filter(f => f.status === 'Under Maintenance').length, icon: icons.alertCircle },
                    ]}
                    onAdd={hasActiveContract ? () => { setFleetEditData(null); setActivePage('create-fleet'); } : null}
                    onEdit={hasActiveContract ? (row) => { setFleetEditData(row); setActivePage('create-fleet'); } : null}
                    onDelete={hasActiveContract ? (row) => handleDelete('fleet', row) : null}
                    columns={[
                        { key: 'vehicleType', label: 'Type' },
                        { key: 'registrationNumber', label: 'Plate No.' },
                        { key: 'make', label: 'Make' },
                        { key: 'model', label: 'Model' },
                        { key: 'year', label: 'Year' },
                        { key: 'fuelType', label: 'Fuel' },
                        {
                            key: 'color', label: 'Color', render: (v) => v ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: v, border: '1px solid #e2e8f0', display: 'inline-block' }} />
                                    {v}
                                </span>
                            ) : '—'
                        },
                        {
                            key: 'status', label: 'Status', render: v => {
                                const cfg = {
                                    'Active': { bg: '#ecfdf5', color: '#065f46' },
                                    'Under Review': { bg: '#fef9c3', color: '#92400e' },
                                    'Inactive': { bg: '#f1f5f9', color: '#475569' },
                                    'Under Maintenance': { bg: '#dbeafe', color: '#1e40af' },
                                    'Damaged': { bg: '#fee2e2', color: '#991b1b' },
                                    'Retired': { bg: '#f3f4f6', color: '#6b7280' },
                                    'In Transit': { bg: '#fef3c7', color: '#92400e' },
                                }[v] || { bg: '#f1f5f9', color: '#475569' };
                                return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color }}>{v || 'Under Review'}</span>;
                            }
                        },
                    ]}
                    rows={fleet}
                />;
            case 'drivers':
                return <TablePage
                    title="Manage Drivers"
                    icon={icons.drivers}
                    emptyMsg="No drivers registered under your account yet."
                    kpis={[
                        { label: 'Total Drivers', value: drivers.length, icon: icons.drivers },
                        { label: 'Active Drivers', value: drivers.filter(d => d.status === 'Active').length, icon: icons.check },
                        { label: 'Pending Review', value: drivers.filter(d => d.status !== 'Active').length, icon: icons.alertCircle },
                    ]}
                    onAdd={hasActiveContract ? () => { setDriverEditData(null); setActivePage('create-driver'); } : null}
                    onEdit={hasActiveContract ? (row) => { setDriverEditData(row); setActivePage('create-driver'); } : null}
                    onDelete={hasActiveContract ? (row) => handleDelete('drivers', row) : null}
                    columns={[
                        { key: 'name', label: 'Name' },
                        { key: 'phoneNumber', label: 'Phone' },
                        { key: 'email', label: 'Email' },
                        {
                            key: 'status', label: 'Status', render: v => (
                                <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: v === 'Active' ? '#ecfdf5' : '#fef9c3', color: v === 'Active' ? '#065f46' : '#92400e' }}>{v || 'Active'}</span>
                            )
                        },
                    ]}
                    rows={drivers}
                />;
            case 'create-driver':
                return (
                    <div style={{ padding: '0px 0px', flex: 1, overflowY: 'auto' }}>
                        <CreateDriver
                            user={{ userType: 'vendor', _id: initialVendor._id, operatorId: operatorDetails._id }}
                            editDriver={driverEditData}
                            onBack={() => setActivePage('drivers')}
                            onSaved={() => { setActivePage('drivers'); loadData(); }}
                            operatorDetails={operatorDetails}
                            vendorOnly={true}
                        />
                    </div>
                );
            case 'create-fleet':
                return (
                    <div style={{ padding: '0px 0px', flex: 1, overflowY: 'auto' }}>
                        <CreateVendorFleet
                            user={{ userType: 'vendor', _id: initialVendor._id, operatorId: operatorDetails._id }}
                            editVehicle={fleetEditData}
                            onBack={() => setActivePage('fleet')}
                            onSaved={() => { setActivePage('fleet'); loadData(); }}
                            operatorDetails={operatorDetails}
                            masterData={masterData}
                        />
                    </div>
                );
            case 'documents':
                return <TablePage
                    title="Documents"
                    icon={icons.docs}
                    emptyMsg="No documents uploaded yet."
                    kpis={[
                        { label: 'Total Documents', value: docs.length, icon: icons.docs },
                        { label: 'Verified', value: docs.filter(d => d.status === 'Valid' || d.status === 'Verified').length, icon: icons.check },
                        { label: 'Pending Review', value: docs.filter(d => d.status === 'Under Review' || d.status === 'Pending').length, icon: icons.alertCircle },
                    ]}
                    onAdd={hasActiveContract ? () => handleAction('docs') : null}
                    onEdit={hasActiveContract ? (row) => handleAction('docs', row) : null}
                    onDelete={hasActiveContract ? (row) => handleDelete('docs', row) : null}
                    columns={[
                        { key: 'documentType', label: 'Type' },
                        { key: 'description', label: 'Description' },
                        { key: 'expiryDate', label: 'Expiry', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                        {
                            key: 'status', label: 'Status', render: v => (
                                <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: v === 'Valid' ? '#ecfdf5' : v === 'Under Review' ? '#fef9c3' : '#fef2f2', color: v === 'Valid' ? '#065f46' : v === 'Under Review' ? '#92400e' : '#dc2626' }}>{v || '—'}</span>
                            )
                        },
                    ]}
                    rows={docs}
                />;
            case 'contracts':
                return <TablePage
                    title="Contracts"
                    icon={icons.contracts}
                    emptyMsg="No contracts found for your account."
                    kpis={[
                        { label: 'Total Contracts', value: contracts.length, icon: icons.contracts },
                        { label: 'Active', value: contracts.filter(c => c.status === 'Active').length, icon: icons.check },
                        { label: 'Under Review', value: contracts.filter(c => c.status === 'Under Review').length, icon: icons.alertCircle },
                    ]}
                    onAdd={hasActiveContract ? () => handleAction('contracts') : null}
                    columns={[
                        { key: 'contractType', label: 'Type' },
                        { key: 'startDate', label: 'Start', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                        { key: 'endDate', label: 'End', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                        { key: 'value', label: 'Value', render: v => v ? formatCurrency(Number(v), operatorDetails?.country) : '—' },
                        {
                            key: 'status', label: 'Status', render: v => (
                                <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: v === 'Active' ? '#ecfdf5' : v === 'Under Review' ? '#fef9c3' : '#f1f5f9', color: v === 'Active' ? '#065f46' : v === 'Under Review' ? '#92400e' : '#475569' }}>{v || '—'}</span>
                            )
                        },
                    ]}
                    rows={contracts}
                />;
            case 'pricing':
                return <TablePage
                    title="Manage Pricing"
                    icon={icons.pricing}
                    emptyMsg="No pricing rates configured yet."
                    kpis={[
                        { label: 'Service Routes', value: pricing.length, icon: icons.pricing },
                        { label: 'Active Rates', value: pricing.filter(p => p.status === 'Active').length, icon: icons.check },
                        { label: 'Pending Approval', value: pricing.filter(p => p.status !== 'Active').length, icon: icons.alertCircle },
                    ]}
                    onAdd={hasActiveContract ? () => handleAction('pricing') : null}
                    onEdit={hasActiveContract ? (row) => handleAction('pricing', row) : null}
                    onDelete={hasActiveContract ? (row) => handleDelete('pricing', row) : null}
                    columns={[
                        { key: 'serviceType', label: 'Service' },
                        { key: 'basePrice', label: 'Base Price', render: v => formatCurrency(Number(v) || 0, operatorDetails?.country) },
                        { key: 'perKmPrice', label: 'Per KM', render: v => formatCurrency(Number(v) || 0, operatorDetails?.country) },
                        { key: 'surgePrice', label: 'Surge', render: v => formatCurrency(Number(v) || 0, operatorDetails?.country) },
                        {
                            key: 'status', label: 'Status', render: v => (
                                <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: v === 'Active' ? '#ecfdf5' : v === 'Under Review' ? '#fef9c3' : '#f1f5f9', color: v === 'Active' ? '#065f46' : v === 'Under Review' ? '#92400e' : '#475569' }}>{v || '—'}</span>
                            )
                        },
                    ]}
                    rows={pricing}
                />;
            case 'profile':
                return <ProfilePage vendor={vendorData} onUpdate={handleProfileUpdate} />;
            default:
                return null;
        }
    };

    const hasActiveContract = contracts.some(c => c.status === 'Active');

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f1f5f9', fontFamily: FONT }}>
            <VendorSidebar
                activePage={activePage}
                onNavigate={setActivePage}
                vendor={vendorData}
                onLogout={onLogout}
                operatorDetails={operatorDetails}
                badges={{ pendingTrips: trips.filter(t => t.vendorStatus === 'Pending Vendor').length }}
                notifications={notifications}
                onMarkAllRead={async () => {
                    await fetch(`/api/notifications/mark-all-read/vendor/${vendorId}`, { method: 'PUT' });
                    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                }}
                onMarkRead={async (id) => {
                    try {
                        await fetch(`/api/notifications/${id}/read`, { method: 'PUT' });
                        setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
                    } catch (err) { console.error(err); }
                }}
                hasActiveContract={hasActiveContract}
            />
            <main style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {/* ── Topbar / Header ── */}
                <div style={{ padding: '16px 28px', background: 'white', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 6h16M4 12h16M4 18h16"></path>
                            </svg>
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#1e3a8a', textTransform: 'capitalize', letterSpacing: '0.01em' }}>
                            {activePage === 'overview' ? 'Dashboard Overview' : activePage.replace('-', ' ')}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>
                            {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })} &middot; {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>

                    </div>
                </div>
                {trips.filter(t => t.vendorStatus === 'Pending Vendor').length > 0 && (
                    <div 
                        onClick={() => setActivePage('trips')}
                        style={{ 
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)', 
                            color: 'white', 
                            padding: '14px 40px', 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            flexShrink: 0,
                            boxShadow: '0 4px 12px rgba(239,68,68,0.2)'
                        }}
                        className="vendor-header-alert"
                    >
                        <style>{`
                            .vendor-header-alert:hover { background: linear-gradient(135deg, #f87171, #ef4444) !important; }
                        `}</style>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Icon d={icons.alertCircle} size={20} />
                            <span style={{ fontWeight: 800, fontSize: 14, letterSpacing: '0.02em' }}>
                                Awaiting Response: You have {trips.filter(t => t.vendorStatus === 'Pending Vendor').length} trip(s) pending your action!
                            </span>
                        </div>
                        <button style={{ background: 'white', color: '#ef4444', border: 'none', padding: '8px 20px', borderRadius: 24, fontWeight: 800, fontSize: 12, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            View Trips
                        </button>
                    </div>
                )}
                
                {renderPage()}
            </main>

            {modal.show && (
                <Modal
                    title={`${modal.data?._id ? 'Edit' : 'Add'} ${modal.type.charAt(0).toUpperCase() + modal.type.slice(1)}`}
                    onClose={() => { setModal({ show: false, type: '', data: null }); setDocumentsList([{ name: '', file: null }]); }}
                    onSave={handleSave}
                    loading={saving}
                    maxWidth={modal.type === 'fleet' ? 680 : 520}
                >
                    <div style={{ display: 'grid', gap: 16 }}>
                        {modal.type === 'fleet' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                <style>{`
                                    .vf-section-label { font-size: 11px; font-weight: 800; color: #00B2A9; text-transform: uppercase; letter-spacing: 0.08em; margin: 24px 0 14px; padding-bottom: 6px; border-bottom: 1.5px solid #e6f0fa; }
                                    .vf-section-label:first-child { margin-top: 0; }
                                    .vf-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                                    .vf-form-group { display: flex; flex-direction: column; gap: 8px; }
                                    .vf-form-group.full { grid-column: span 2; }
                                    .vf-form-label { font-size: 13px; font-weight: 700; color: #475569; display: flex; align-items: center; gap: 6px; }
                                    .vf-input-wrapper { position: relative; display: flex; align-items: center; }
                                    .vf-input-icon { position: absolute; left: 14px; color: #94a3b8; pointer-events: none; }
                                    .vf-form-input { 
                                        width: 100%; padding: 12px 16px 12px 42px; border: 2px solid #e2e8f0; border-radius: 14px; font-size: 14px; 
                                        background: #fcfdfe; outline: none; transition: all 0.2s; font-family: 'Inter', sans-serif;
                                        color: #1e293b; box-sizing: border-box; font-weight: 500;
                                    }
                                    .vf-form-input:focus { border-color: #00B2A9; box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08); background: #fff; }
                                    .vf-form-input:focus + .vf-input-icon { color: #00B2A9; }
                                `}</style>

                                {/* ── Section: Identification ── */}
                                <div className="vf-section-label">Vehicle Identification</div>
                                <div className="vf-form-grid">
                                    <div className="vf-form-group full">
                                        <label className="vf-form-label">Vehicle Type *</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.truck} size={16} className="vf-input-icon" />
                                            <select className="vf-form-input" required value={modal.data.vehicleType || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, vehicleType: e.target.value } })}>
                                                <option value="">Select type</option>
                                                {(masterData['vehicle-types'] || []).map(vt => (
                                                    <option key={vt._id} value={vt.name}>{vt.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Vehicle ID / Code</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.truck} size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" placeholder="e.g. V-001" value={modal.data.vehicleCode || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, vehicleCode: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Registration Number *</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.search} size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" required placeholder="e.g. DXB-12345" value={modal.data.registrationNumber || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, registrationNumber: e.target.value } })} />
                                        </div>
                                    </div>
                                </div>

                                {/* ── Photo ── */}
                                <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 80, height: 80, borderRadius: 16, background: '#f1f5f9', border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                                        {modal.data.photo ? (
                                            <img src={modal.data.photo} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Icon d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" size={28} color="#94a3b8" />
                                        )}
                                        <input 
                                            type="file" accept="image/*"
                                            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onload = (evt) => setModal({ ...modal, data: { ...modal.data, photo: evt.target.result } });
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <h4 style={{ margin: '0 0 4px', fontSize: 13, color: '#1e293b', fontWeight: 600 }}>Upload Photo</h4>
                                        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Click or drag image to upload.</p>
                                    </div>
                                </div>

                                {/* ── Section: Vehicle Details ── */}
                                <div className="vf-section-label">Vehicle Details</div>
                                <div className="vf-form-grid">
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Make *</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.truck} size={16} className="vf-input-icon" style={{ opacity: 0.5 }} />
                                            <select className="vf-form-input" required value={modal.data.make || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, make: e.target.value } })}>
                                                <option value="">Select Make...</option>
                                                {(masterData['vehicle-makes'] || []).map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                                {modal.data.make && !(masterData['vehicle-makes'] || []).find(m => m.name === modal.data.make) && <option value={modal.data.make}>{modal.data.make}</option>}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Model</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.truck} size={16} className="vf-input-icon" style={{ opacity: 0.5 }} />
                                            <select className="vf-form-input" value={modal.data.model || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, model: e.target.value } })}>
                                                <option value="">Select Model...</option>
                                                {(masterData['vehicle-models'] || []).map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                                {modal.data.model && !(masterData['vehicle-models'] || []).find(m => m.name === modal.data.model) && <option value={modal.data.model}>{modal.data.model}</option>}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Year</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="number" min="1990" max={new Date().getFullYear() + 1} placeholder={new Date().getFullYear()} value={modal.data.year || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, year: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Fuel Type</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M13 2a2 2 0 0 0-2 2v1H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4V4a2 2 0 0 0-2-2z" size={16} className="vf-input-icon" />
                                            <select className="vf-form-input" value={modal.data.fuelType || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, fuelType: e.target.value } })}>
                                                <option value="">Select fuel type</option>
                                                <option>Petrol</option>
                                                <option>Diesel</option>
                                                <option>Electric</option>
                                                <option>Hybrid</option>
                                                <option>CNG</option>
                                                <option>LPG</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Color</label>
                                        <div className="vf-input-wrapper" style={{ gap: 10 }}>
                                            <input type="color" value={modal.data.color || '#ffffff'} onChange={e => setModal({ ...modal, data: { ...modal.data, color: e.target.value } })} style={{ width: 42, height: 42, border: '2px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', padding: 2, background: 'none' }} />
                                            <input className="vf-form-input" style={{ paddingLeft: 16 }} placeholder="e.g. White" value={modal.data.color || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, color: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Fleet Received Date</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="date" value={modal.data.fleetReceivedDate} onChange={e => setModal({ ...modal, data: { ...modal.data, fleetReceivedDate: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Seating Capacity</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.truck} size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="number" placeholder="e.g. 15" value={modal.data.seatingCapacity || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, seatingCapacity: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Fuel Sticker Number</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.search} size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" placeholder="e.g. FS-123" value={modal.data.fuelStickerNumber || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, fuelStickerNumber: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Efficiency</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M13 2a2 2 0 0 0-2 2v1H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4V4a2 2 0 0 0-2-2z" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" placeholder="e.g. 15 km/l" value={modal.data.efficiency || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, efficiency: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Status</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.truck} size={16} className="vf-input-icon" style={{ color: modal.data.status === 'Active' ? '#10b981' : '#94a3b8' }} />
                                            <select className="vf-form-input" value={modal.data.status || 'Under Review'} onChange={e => setModal({ ...modal, data: { ...modal.data, status: e.target.value } })}>
                                                <option value="Under Review">Under Review</option>
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                                <option value="Under Maintenance">Under Maintenance</option>
                                                <option value="Damaged">Damaged</option>
                                                <option value="Retired">Retired</option>
                                                <option value="In Transit">In Transit</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Section: Registration & Insurance ── */}
                                <div className="vf-section-label">Registration & License</div>
                                <div className="vf-form-grid">
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Registration Expiry Date</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="date" value={modal.data.registrationExpiry || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, registrationExpiry: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Vehicle License / Permit No.</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.search} size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" placeholder="e.g. LIC-9988" value={modal.data.vehicleLicenseNumber || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, vehicleLicenseNumber: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">License Expiry Date</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="date" value={modal.data.vehicleLicenseExpiry || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, vehicleLicenseExpiry: e.target.value } })} />
                                        </div>
                                    </div>
                                </div>

                                <div className="vf-section-label">Insurance Details</div>
                                <div className="vf-form-grid">
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Insurance Provider</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.search} size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" placeholder="e.g. Al Ahliya" value={modal.data.insuranceProvider || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, insuranceProvider: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Policy Number</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d={icons.search} size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" placeholder="e.g. POL-123456" value={modal.data.policyNumber || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, policyNumber: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Insurance Expiry Date</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="date" value={modal.data.insuranceExpiry || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, insuranceExpiry: e.target.value } })} />
                                        </div>
                                    </div>
                                </div>

                                {/* ── Section: Financial Details ── */}
                                <div className="vf-section-label">Financial Details</div>
                                <div className="vf-form-grid">
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Purchase Price</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="number" step="0.01" placeholder="0.00" value={modal.data.purchasePrice || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, purchasePrice: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Acquisition Date</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="date" value={modal.data.acquisitionDate ? modal.data.acquisitionDate.slice(0, 10) : ''} onChange={e => setModal({ ...modal, data: { ...modal.data, acquisitionDate: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Depreciation / Month</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="number" step="0.01" placeholder="0.00" value={modal.data.depreciationMonthly || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, depreciationMonthly: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">GPS Fee / Month</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="number" step="0.01" placeholder="0.00" value={modal.data.gpsMonthly || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, gpsMonthly: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Dashcam Fee / Month</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="number" step="0.01" placeholder="0.00" value={modal.data.dashcamMonthly || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, dashcamMonthly: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Insurance / Month</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="number" step="0.01" placeholder="0.00" value={modal.data.insuranceMonthly || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, insuranceMonthly: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Other Costs / Month</label>
                                        <div className="vf-input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="vf-input-icon" />
                                            <input className="vf-form-input" type="number" step="0.01" placeholder="0.00" value={modal.data.otherCostsMonthly || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, otherCostsMonthly: e.target.value } })} />
                                        </div>
                                    </div>
                                    <div className="vf-form-group">
                                        <label className="vf-form-label">Monthly Total</label>
                                        <div style={{ padding: '12px 16px', borderRadius: 14, background: 'linear-gradient(135deg,#eef2ff,#e6f0fa)', border: '1.5px solid #c3dcf5', fontWeight: 800, fontSize: 17, color: '#11427D', textAlign: 'center' }}>
                                            {(
                                                (parseFloat(modal.data.depreciationMonthly) || 0) +
                                                (parseFloat(modal.data.gpsMonthly) || 0) +
                                                (parseFloat(modal.data.dashcamMonthly) || 0) +
                                                (parseFloat(modal.data.insuranceMonthly) || 0) +
                                                (parseFloat(modal.data.otherCostsMonthly) || 0)
                                            ).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {modal.type === 'drivers' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '10px 0' }}>
                                {/* Basic Information */}
                                <div>
                                    <h4 style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>Basic Information</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div style={{ ...formStyle.group, gridColumn: 'span 2' }}>
                                            <label style={formStyle.label}>Driver Name</label>
                                            <input style={formStyle.input} value={modal.data.name || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, name: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Email Address</label>
                                            <input style={formStyle.input} type="email" value={modal.data.email || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, email: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Country Code</label>
                                            <input style={formStyle.input} value={modal.data.countryCode || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, countryCode: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Phone Number</label>
                                            <input style={formStyle.input} value={modal.data.phoneNumber || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, phoneNumber: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Alt Phone</label>
                                            <input style={formStyle.input} value={modal.data.altPhone || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, altPhone: e.target.value } })} />
                                        </div>
                                    </div>
                                </div>
                                {/* Identity & Documentation */}
                                <div>
                                    <h4 style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>Identity & Documentation</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div style={{ ...formStyle.group, gridColumn: 'span 2' }}>
                                            <label style={formStyle.label}>Nationality</label>
                                            <input style={formStyle.input} value={modal.data.nationality || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, nationality: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Civil ID</label>
                                            <input style={formStyle.input} value={modal.data.civilId || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, civilId: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Civil ID Expiry</label>
                                            <input style={formStyle.input} type="date" value={modal.data.civilIdExpiry ? new Date(modal.data.civilIdExpiry).toISOString().split('T')[0] : ''} onChange={e => setModal({ ...modal, data: { ...modal.data, civilIdExpiry: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Passport Number</label>
                                            <input style={formStyle.input} value={modal.data.passportNumber || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, passportNumber: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Passport Expiry</label>
                                            <input style={formStyle.input} type="date" value={modal.data.passportExpiry ? new Date(modal.data.passportExpiry).toISOString().split('T')[0] : ''} onChange={e => setModal({ ...modal, data: { ...modal.data, passportExpiry: e.target.value } })} />
                                        </div>
                                    </div>
                                </div>
                                {/* Employment Specifics */}
                                <div>
                                    <h4 style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>Employment Specifics</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Driver Code</label>
                                            <input style={formStyle.input} value={modal.data.driverCode || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, driverCode: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>License Number</label>
                                            <input style={formStyle.input} value={modal.data.licenseNumber || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, licenseNumber: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>License Expiry</label>
                                            <input style={formStyle.input} type="date" value={modal.data.licenseExpiry ? new Date(modal.data.licenseExpiry).toISOString().split('T')[0] : ''} onChange={e => setModal({ ...modal, data: { ...modal.data, licenseExpiry: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Onboard Date</label>
                                            <input style={formStyle.input} type="date" value={modal.data.onboardDate ? new Date(modal.data.onboardDate).toISOString().split('T')[0] : ''} onChange={e => setModal({ ...modal, data: { ...modal.data, onboardDate: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Probation Period</label>
                                            <input style={formStyle.input} value={modal.data.probationPeriod || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, probationPeriod: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Accommodation Location</label>
                                            <input style={formStyle.input} value={modal.data.accommodationLocation || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, accommodationLocation: e.target.value } })} />
                                        </div>
                                        <div style={{ ...formStyle.group, gridColumn: 'span 2' }}>
                                            <label style={formStyle.label}>Status</label>
                                            <select style={formStyle.input} value={modal.data.status || 'Active'} onChange={e => setModal({ ...modal, data: { ...modal.data, status: e.target.value } })}>
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                                <option value="On Leave">On Leave</option>
                                                <option value="Resigned">Resigned</option>
                                                <option value="Terminated">Terminated</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                {/* Payroll & Allowances */}
                                <div>
                                    <h4 style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>Payroll & Allowances</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Base Salary</label>
                                            <input style={formStyle.input} type="number" value={modal.data.baseSalary || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, baseSalary: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Fixed Allowances</label>
                                            <input style={formStyle.input} type="number" value={modal.data.fixedAllowances || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, fixedAllowances: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Overtime Rate</label>
                                            <input style={formStyle.input} type="number" value={modal.data.overtimeRate || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, overtimeRate: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Uniform Cost</label>
                                            <input style={formStyle.input} type="number" value={modal.data.uniformCost || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, uniformCost: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Accommodation Cost</label>
                                            <input style={formStyle.input} type="number" value={modal.data.accommodationCost || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, accommodationCost: e.target.value } })} />
                                        </div>
                                        <div style={formStyle.group}>
                                            <label style={formStyle.label}>Other Deduction</label>
                                            <input style={formStyle.input} type="number" value={modal.data.otherDeduction || ''} onChange={e => setModal({ ...modal, data: { ...modal.data, otherDeduction: e.target.value } })} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {modal.type === 'docs' && (
                            <>
                                <div style={formStyle.group}>
                                    <label style={formStyle.label}>Document Type</label>
                                    <select style={formStyle.input} value={modal.data.documentType} onChange={e => setModal({ ...modal, data: { ...modal.data, documentType: e.target.value } })}>
                                        <option value="">Select Type</option>
                                        {(masterData['document-types'] || []).map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                    </select>
                                </div>
                                <div style={formStyle.group}>
                                    <label style={formStyle.label}>Description</label>
                                    <input style={formStyle.input} value={modal.data.description} onChange={e => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })} />
                                </div>
                                <div style={formStyle.group}>
                                    <label style={formStyle.label}>Expiry Date</label>
                                    <input type="date" style={formStyle.input} value={modal.data.expiryDate ? modal.data.expiryDate.split('T')[0] : ''} onChange={e => setModal({ ...modal, data: { ...modal.data, expiryDate: e.target.value } })} />
                                </div>
                            </>
                        )}
                        {modal.type === 'contracts' && (
                            <>
                                <div style={formStyle.group}>
                                    <label style={formStyle.label}>Contract Type</label>
                                    <input style={formStyle.input} value={modal.data.contractType} onChange={e => setModal({ ...modal, data: { ...modal.data, contractType: e.target.value } })} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div style={formStyle.group}>
                                        <label style={formStyle.label}>Start Date</label>
                                        <input type="date" style={formStyle.input} value={modal.data.startDate ? modal.data.startDate.split('T')[0] : ''} onChange={e => setModal({ ...modal, data: { ...modal.data, startDate: e.target.value } })} />
                                    </div>
                                    <div style={formStyle.group}>
                                        <label style={formStyle.label}>End Date</label>
                                        <input type="date" style={formStyle.input} value={modal.data.endDate ? modal.data.endDate.split('T')[0] : ''} onChange={e => setModal({ ...modal, data: { ...modal.data, endDate: e.target.value } })} />
                                    </div>
                                </div>
                                <div style={formStyle.group}>
                                    <label style={formStyle.label}>Contract Value</label>
                                    <input type="number" style={formStyle.input} value={modal.data.value} onChange={e => setModal({ ...modal, data: { ...modal.data, value: e.target.value } })} />
                                </div>
                            </>
                        )}
                        {modal.type === 'pricing' && (
                            <>
                                <div style={formStyle.group}>
                                    <label style={formStyle.label}>Service Type</label>
                                    <select style={formStyle.input} value={modal.data.serviceType} onChange={e => setModal({ ...modal, data: { ...modal.data, serviceType: e.target.value } })}>
                                        <option value="">Select Service</option>
                                        {(masterData['service-types'] || []).map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                    </select>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div style={formStyle.group}>
                                        <label style={formStyle.label}>Base Price</label>
                                        <input type="number" style={formStyle.input} value={modal.data.basePrice} onChange={e => setModal({ ...modal, data: { ...modal.data, basePrice: e.target.value } })} />
                                    </div>
                                    <div style={formStyle.group}>
                                        <label style={formStyle.label}>Per KM Price</label>
                                        <input type="number" style={formStyle.input} value={modal.data.perKmPrice} onChange={e => setModal({ ...modal, data: { ...modal.data, perKmPrice: e.target.value } })} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div style={formStyle.group}>
                                        <label style={formStyle.label}>Surge Price</label>
                                        <input type="number" style={formStyle.input} value={modal.data.surgePrice} onChange={e => setModal({ ...modal, data: { ...modal.data, surgePrice: e.target.value } })} />
                                    </div>
                                    <div style={formStyle.group}>
                                        <label style={formStyle.label}>Status</label>
                                        <select style={formStyle.input} value={modal.data.status} onChange={e => setModal({ ...modal, data: { ...modal.data, status: e.target.value } })}>
                                            <option value="Active">Active</option>
                                            <option value="Under Review">Under Review</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {toast && (
                <div style={{
                    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                    background: toast.type === 'error' ? '#ef4444' : '#1e293b',
                    color: 'white', padding: '12px 24px', borderRadius: 12, fontSize: 14, fontWeight: 600,
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 2000,
                    animation: 'slideUp 0.3s ease-out'
                }}>
                    {toast.msg}
                </div>
            )}
        </div>
    );
}
